// Familia roleta — scroll-driven slot vertical com 5 marcas.
// Portado de qualiapps-lading-page.html linhas 3734-3856.
//
// Brand metadata (cor + href) fixos aqui (CSS var names + anchor).
// Textos (desc + cta) vêm de i18n para PT/EN — `t('familia.brand_${i}_desc')`.
// Em `languageChanged`, re-renderiza a marca atual.

import { t, onLanguageChange } from "../lib/i18n";

type Brand = {
  name: string;
  color: string; // CSS var reference (será aplicado via setProperty)
  soft: string;
  href: string;
};

const BRANDS: readonly Brand[] = [
  { name: "QualiApps", color: "var(--qualiapps-cyan-deep)", soft: "var(--qualiapps-cyan)", href: "#services" },
  { name: "QualiGestão", color: "var(--qualigestao-green)", soft: "var(--qualigestao-green)", href: "#portfolio" },
  { name: "QualiObra", color: "var(--qualiobra-mint)", soft: "var(--qualiobra-mint)", href: "#qualiobra" },
  { name: "QualiBroker", color: "var(--qualibroker-orange)", soft: "var(--qualibroker-orange)", href: "#portfolio" },
  { name: "QualiClub", color: "var(--qualiclub-purple)", soft: "var(--qualiclub-purple)", href: "#contact" },
];

const N = BRANDS.length;
const EDGE_PAD = 0.05; // 5% de hold em cada extremo

export function initFamiliaRoleta(): void {
  const stage = document.getElementById("familia");
  if (!stage) return;

  const sticky = document.getElementById("familiaRoletaSticky");
  const reel = stage.querySelector<HTMLElement>('[data-roleta="reel"]');
  const numEl = stage.querySelector<HTMLElement>('[data-roleta="num"]');
  const descEl = stage.querySelector<HTMLElement>('[data-roleta="desc"]');
  const ctaEl = stage.querySelector<HTMLAnchorElement>('[data-roleta="cta"]');
  const ctaTextEl = stage.querySelector<HTMLElement>('[data-roleta="cta-text"]');
  const srEl = stage.querySelector<HTMLElement>('[data-roleta="sr"]');
  const dotsBar = stage.querySelector<HTMLElement>('[data-roleta="dots"]');
  const dots: HTMLElement[] = dotsBar
    ? Array.from(dotsBar.querySelectorAll<HTMLElement>(".dot"))
    : [];
  const hintEl = stage.querySelector<HTMLElement>('[data-roleta="hint"]');

  if (!sticky || !reel || !numEl || !descEl || !ctaEl || !ctaTextEl) return;

  let current = -1;
  let itemH = 0;

  function measure(): void {
    if (!reel) return;
    const first = reel.querySelector<HTMLElement>(".suffix");
    if (first) itemH = first.getBoundingClientRect().height;
  }

  function setBrand(i: number, fast: boolean): void {
    if (i === current) return;
    current = i;
    const b = BRANDS[i];
    if (!b) return;

    sticky!.style.setProperty("--brand-active", b.color);
    sticky!.style.setProperty("--brand-soft-active", b.soft);
    numEl!.textContent = String(i + 1).padStart(2, "0");

    const desc = t(`familia.brand_${i}_desc`);
    const cta = t(`familia.brand_${i}_cta`);

    descEl!.classList.add("fading");
    const swap = (): void => {
      descEl!.textContent = desc;
      ctaEl!.setAttribute("href", b.href);
      ctaTextEl!.textContent = cta;
      if (srEl) srEl.textContent = b.name;
      requestAnimationFrame(() => descEl!.classList.remove("fading"));
    };
    if (fast) swap();
    else window.setTimeout(swap, 180);

    dots.forEach((d, k) => d.classList.toggle("active", k === i));
  }

  function update(): void {
    if (!stage || !reel) return;
    const rect = stage.getBoundingClientRect();
    const total = Math.max(1, rect.height - window.innerHeight);
    const scrolled = Math.min(total, Math.max(0, -rect.top));
    const p = scrolled / total;

    // mapear progresso com hold de EDGE_PAD em cada extremo
    let q = (p - EDGE_PAD) / (1 - 2 * EDGE_PAD);
    q = Math.max(0, Math.min(1, q));
    const tIdx = q * (N - 1);
    const idx = Math.round(tIdx);

    if (itemH > 0) {
      reel.style.transform = `translateY(${(-tIdx * itemH).toFixed(2)}px)`;
    }

    setBrand(idx, current === -1);

    if (hintEl) {
      if (p > 0.04) hintEl.classList.add("gone");
      else hintEl.classList.remove("gone");
    }
  }

  let raf = 0;
  let lastScrollY = -1;

  function onScroll(): void {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      update();
      lastScrollY = window.scrollY;
    });
  }

  function onResize(): void {
    measure();
    update();
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize);

  // Safety net: IntersectionObserver + poll para o caso de scroll/rAF throttle.
  let pollId = 0;
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !pollId) {
          pollId = window.setInterval(() => {
            if (window.scrollY !== lastScrollY) {
              lastScrollY = window.scrollY;
              update();
            }
          }, 80);
        } else if (!e.isIntersecting && pollId) {
          window.clearInterval(pollId);
          pollId = 0;
        }
      }
    },
    { rootMargin: "20% 0%" },
  );
  io.observe(stage);

  // Click + Enter/Space para jump-to-brand.
  function jumpTo(i: number): void {
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const q = N > 1 ? i / (N - 1) : 0;
    const p = q * (1 - 2 * EDGE_PAD) + EDGE_PAD;
    const targetY = window.scrollY + rect.top + p * total;
    window.scrollTo({ top: targetY, behavior: "smooth" });
  }
  dots.forEach((d) => {
    const i = Number(d.dataset.i ?? "0");
    d.addEventListener("click", () => jumpTo(i));
    d.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        jumpTo(i);
      }
    });
  });

  // i18n re-render on language change: força refresh dos textos da marca atual.
  onLanguageChange(() => {
    if (current >= 0) {
      const i = current;
      current = -1; // força re-aplicação
      setBrand(i, true);
    }
  });

  measure();
  update();
}
